import { createContext, useContext, useCallback, useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import { toast } from '@/hooks/useToast';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';

type VoiceStatus = 'connected' | 'connecting' | 'disconnected';

interface VoiceControlContextType {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  status: VoiceStatus;
  isSpeaking: boolean;
  isConnecting: boolean;
}

const noopContext: VoiceControlContextType = {
  connect: async () => {},
  disconnect: async () => {},
  status: 'disconnected',
  isSpeaking: false,
  isConnecting: false,
};

const VoiceControlContext = createContext<VoiceControlContextType | undefined>(undefined);

function ActiveVoiceControlProvider({ children }: Readonly<{ children: ReactNode }>) {
  const orgNavigate = useOrgNavigate();
  const { selectedOrganization } = useOrganization();
  const [isConnecting, setIsConnecting] = useState(false);
  const isMountedRef = useRef(true);

  const conversation = useConversation({
    clientTools: {
      navigateTo: ({ page }: { page: string }) => {
        console.log('[VoiceControl] navigateTo called with:', page);
        orgNavigate(page);
        return `Navigated to ${page}`;
      },
      showNotification: ({ message, type }: { message: string; type: string }) => {
        console.log('[VoiceControl] showNotification called:', message, type);
        if (type === 'success') toast.success(message);
        else if (type === 'warning') toast.warning(message);
        else toast.info(message);
        return 'Notification shown';
      },
    },
    onConnect: () => {
      console.log('[VoiceControl] connected');
    },
    onDisconnect: () => {
      console.log('[VoiceControl] disconnected');
    },
    onStatusChange: (status) => {
      console.log('[VoiceControl] status changed:', status);
    },
    onModeChange: (mode) => {
      console.log('[VoiceControl] mode changed:', mode.mode);
    },
    onMessage: (message) => {
      console.log('[VoiceControl] message:', message);
    },
    onError: (error: unknown) => {
      console.error('Voice control error:', error);
      toast.error('Voice control encountered an error');
    },
    onUnhandledClientToolCall: (toolCall) => {
      console.warn('[VoiceControl] unhandled tool call:', toolCall);
    },
  });

  // Ref so callbacks and cleanup can access the latest conversation without
  // appearing in dependency arrays (avoids stale closures and effect re-runs)
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  const connect = useCallback(async () => {
    setIsConnecting(true);
    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Get signed URL from edge function
      const { data, error } = await supabase.functions.invoke('elevenlabs-token', {
        body: { organization_id: selectedOrganization?.id },
      });
      if (error || !data?.signedUrl) {
        console.error('Token fetch failed:', error);
        if (error) throw error;
        throw new Error('Failed to get voice session token');
      }

      // Start the WebSocket session with the signed URL
      await conversationRef.current.startSession({
        signedUrl: data.signedUrl,
      });

      if (isMountedRef.current) {
        toast.success('Voice control connected');
      }
    } catch (err) {
      console.error('Failed to connect voice control:', err);

      if (isMountedRef.current) {
        if (err instanceof DOMException) {
          if (err.name === 'NotAllowedError') {
            toast.error('Microphone permission denied. Please allow access in your browser settings.');
          } else if (err.name === 'NotFoundError') {
            toast.error('No microphone found. Please connect a microphone and try again.');
          } else {
            toast.error(`Microphone error: ${err.message}`);
          }
        } else {
          toast.error('Failed to start voice control');
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsConnecting(false);
      }
    }
  }, [selectedOrganization?.id]);

  const disconnect = useCallback(async () => {
    await conversationRef.current.endSession();
    toast.info('Voice control disconnected');
  }, []);

  // Track mounted state
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clean up on unmount only (org switch, logout, navigation)
  useEffect(() => {
    return () => {
      const conv = conversationRef.current;
      if (conv.status === 'connected') {
        conv.endSession().catch((err) => {
          console.error('Failed to end session during cleanup:', err);
        });
      }
    };
  }, []);

  // Clean up on tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      const conv = conversationRef.current;
      if (conv.status === 'connected') {
        conv.endSession().catch((err) => {
          console.error('Failed to end session on beforeunload:', err);
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const derivedStatus: VoiceStatus = isConnecting
    ? 'connecting'
    : (conversation.status as VoiceStatus);

  const value = useMemo(
    () => ({
      connect,
      disconnect,
      status: derivedStatus,
      isSpeaking: conversation.isSpeaking,
      isConnecting,
    }),
    [connect, disconnect, derivedStatus, conversation.isSpeaking, isConnecting]
  );

  return (
    <VoiceControlContext.Provider value={value}>
      {children}
    </VoiceControlContext.Provider>
  );
}

export function VoiceControlProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { enabled: voiceEnabled } = useFeatureEnabled('voice_control');

  // When feature is disabled, provide a no-op context — skips ElevenLabs SDK initialization
  if (!voiceEnabled) {
    return (
      <VoiceControlContext.Provider value={noopContext}>
        {children}
      </VoiceControlContext.Provider>
    );
  }

  return <ActiveVoiceControlProvider>{children}</ActiveVoiceControlProvider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVoiceControl() {
  const context = useContext(VoiceControlContext);
  if (context === undefined) {
    throw new Error('useVoiceControl must be used within a VoiceControlProvider');
  }
  return context;
}
